import React, { useEffect, useMemo, useRef, useState } from "react";
import { OverflowImageRowProps } from "./types";
import { IMAGE_SIZE, GAP } from "./constants";
import { useStyles } from "./styles";

const OverflowImageRow: React.FC<OverflowImageRowProps> = ({ items }) => {
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxSlots, setMaxSlots] = useState(0);

  // Flatten items by quantity
  const images = useMemo(() => {
    return items.flatMap((item) =>
      Array.from({ length: item.quantity ?? 1 }).map((_, index) => ({
        key: `${item.id}-${index}`,
        src: item.imageUrl,
      }))
    );
  }, [items]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const calculateSlots = (width: number) => {
      /**
       * n * size + (n - 1) * gap <= width
       * n <= (width + gap) / (size + gap)
       */
      const slots = Math.floor((width + GAP) / (IMAGE_SIZE + GAP));
      setMaxSlots(Math.max(0, slots));
    };

    calculateSlots(el.getBoundingClientRect().width);

    const observer = new ResizeObserver(([entry]) => {
      calculateSlots(entry.contentRect.width);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const total = images.length;

  if (maxSlots <= 0) {
    return <div ref={containerRef} className={classes.root} />;
  }

  const hasOverflow = total > maxSlots;

  // Reserve ONE slot for +X when overflowing
  const visibleCount = hasOverflow ? Math.max(0, maxSlots - 1) : total;

  const visibleImages = images.slice(0, visibleCount);
  const overflow = total - visibleImages.length;

  return (
    <div ref={containerRef} className={classes.root} style={{ gap: GAP }}>
      {visibleImages.map((img) => (
        <img key={img.key} src={img.src} alt="" className={classes.image} />
      ))}

      {overflow > 0 && <div className={classes.overflowBadge}>+{overflow}</div>}
    </div>
  );
};

export default OverflowImageRow;
